import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Pagination from "@mui/material/Pagination";
import { token_api } from "../../Utils/Network";

const cardSx = {
  background: "#fff",
  borderRadius: "14px",
  boxShadow: "0 1px 3px rgba(16,24,40,.06), 0 1px 2px rgba(16,24,40,.08)",
  border: "1px solid #eef0f4",
  p: { xs: 2, md: 3 },
};

const cell = { padding: "10px 8px", borderTop: "1px solid #f3f4f6", fontSize: 14 };

/**
 * Every session up to today with its report: which still need one (pending)
 * and which have a PDF (done).
 */
const Reports = () => {
  const navigate = useNavigate();
  const [state, setState] = useState("pending");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ data: [], max_pages: 1, total_count: 0 });
  const [loading, setLoading] = useState(true);

  // Wait for typing to pause before searching.
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams({ state, page });
    if (query) params.set("search", query);
    token_api
      .get(`counseling/v1/session_report/panel_list/?${params}`)
      .then((res) => alive && setResult(res.data))
      .catch((err) => console.error("Failed to load reports:", err))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [state, page, query]);

  const openSession = (row) =>
    navigate(`/dashboard/students/${row.student_id}?session=${row.session_id}`);

  return (
    <div style={{ background: "#f5f7fb", minHeight: "100%", padding: "24px 0" }}>
      <div style={{ maxWidth: 1120, margin: "0 auto", padding: "0 16px" }}>
        <Box sx={cardSx}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <h2 style={{ color: "#313E6C", margin: 0 }}>
              Reports ({result.total_count ?? 0})
            </h2>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={state}
                onChange={(_, v) => {
                  if (v !== null) {
                    setState(v);
                    setPage(1);
                  }
                }}
              >
                <ToggleButton value="pending">Pending</ToggleButton>
                <ToggleButton value="done">Generated</ToggleButton>
                <ToggleButton value="">All</ToggleButton>
              </ToggleButtonGroup>
              <TextField
                size="small"
                placeholder="Search name or phone"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "#696984", fontSize: 13 }}>
                  <th style={{ padding: 8 }}>Date</th>
                  <th style={{ padding: 8 }}>Student</th>
                  <th style={{ padding: 8 }}>Session</th>
                  <th style={{ padding: 8 }}>Report</th>
                  <th style={{ padding: 8 }} />
                </tr>
              </thead>
              <tbody>
                {result.data.map((row) => (
                  <tr key={row.booking_id}>
                    <td style={cell}>{row.date}</td>
                    <td style={cell}>
                      {row.student_name}
                      <div style={{ color: "#999", fontSize: 12 }}>{row.phone}</div>
                    </td>
                    <td style={cell}>
                      {row.session_name}
                      {row.package && (
                        <div style={{ color: "#999", fontSize: 12 }}>{row.package}</div>
                      )}
                    </td>
                    <td style={cell}>
                      {row.report?.pdf ? (
                        <>
                          <span style={{ color: "#15803d", fontWeight: 600 }}>Generated</span>
                          <div style={{ color: "#999", fontSize: 12 }}>
                            {row.report.template} · {row.report.generated_on}
                          </div>
                        </>
                      ) : (
                        <span style={{ color: "#b45309", fontWeight: 600 }}>Pending</span>
                      )}
                    </td>
                    <td style={{ ...cell, whiteSpace: "nowrap", textAlign: "right" }}>
                      {row.report?.pdf && (
                        <Button size="small" onClick={() => window.open(row.report.pdf, "_blank")}>
                          View PDF
                        </Button>
                      )}
                      <Button size="small" onClick={() => openSession(row)}>
                        {row.report?.pdf ? "Edit" : "Write report"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!loading && !result.data.length && (
            <p style={{ color: "#999", fontSize: 14, padding: 8 }}>
              {state === "pending" ? "No pending reports. 🎉" : "Nothing here."}
            </p>
          )}
          {loading && <p style={{ color: "#999", fontSize: 14, padding: 8 }}>Loading…</p>}

          {result.max_pages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination count={result.max_pages} page={page} onChange={(_, p) => setPage(p)} />
            </Box>
          )}
        </Box>
      </div>
    </div>
  );
};

export default Reports;
